import React from 'react';
import { func, array, string, bool, shape, number } from 'prop-types';
import classNames from 'classnames';

import { AmenityPill } from '../../components';
import css from './AmenityCategoryContent.module.css';

const AmenityCategoryContent = props => {
  const {
    amenities,
    selectedAmenities,
    customAmenities,
    wifiSpeeds,
    onAmenitySelect,
    onAmenityDeselect,
    onAddCustomClick,
    onCustomAmenityRemove,
    className,
    rootClassName,
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  // Filter out "Other" from amenities and sort alphabetically by name
  const filteredAmenities = amenities.filter(
    a => a.name.toLowerCase() !== 'other'
  );
  const sortedAmenities = [...filteredAmenities].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  const handlePillClick = amenity => {
    const isSelected = selectedAmenities.some(
      selected => selected.id === amenity.id
    );
    if (isSelected) {
      onAmenityDeselect(amenity);
    } else {
      onAmenitySelect(amenity);
    }
  };

  const handleCustomPillRemove = (e, customId) => {
    e.stopPropagation(); // Prevent pill click
    onCustomAmenityRemove(customId);
  };

  /**
   * Get the label for an amenity pill
   * For wifi amenity with speeds, format as "↓ [download] Mbps / ↑ [upload] Mbps"
   */
  const getAmenityLabel = amenity => {
    if (
      amenity.id === 'wifi' &&
      wifiSpeeds &&
      (wifiSpeeds.download || wifiSpeeds.upload)
    ) {
      const downloadText = wifiSpeeds.download
        ? `↓ ${wifiSpeeds.download} Mbps`
        : '';
      const uploadText = wifiSpeeds.upload ? `↑ ${wifiSpeeds.upload} Mbps` : '';

      if (downloadText && uploadText) {
        return `${downloadText} / ${uploadText}`;
      } else if (downloadText) {
        return downloadText;
      } else if (uploadText) {
        return uploadText;
      }
    }
    return amenity.name;
  };

  return (
    <div className={classes}>
      <div className={css.divider} />
      <div className={css.pillContainer}>
        {/* Standard amenity pills */}
        {sortedAmenities.map(amenity => {
          const isSelected = selectedAmenities.some(
            selected => selected.id === amenity.id
          );
          const label = getAmenityLabel(amenity);
          return (
            <AmenityPill
              key={amenity.id}
              amenity={amenity}
              isSelected={isSelected}
              label={label}
              onClick={() => handlePillClick(amenity)}
            />
          );
        })}

        {/* Custom amenity pills */}
        {customAmenities.map(customAmenity => (
          <button
            key={customAmenity.id}
            type="button"
            className={classNames(css.customPill, css.selected)}
            onClick={() => { }} // Custom amenities are always selected, no toggle
          >
            <span className={css.label}>
              {customAmenity.name.charAt(0).toUpperCase() +
                customAmenity.name.slice(1).toLowerCase()}
            </span>
            <span
              className={css.deleteIcon}
              onClick={e => handleCustomPillRemove(e, customAmenity.id)}
              role="button"
              tabIndex={0}
              aria-label={`Remove ${customAmenity.name}`}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleCustomPillRemove(e, customAmenity.id);
                }
              }}
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 1L1 9M1 1L9 9"
                  stroke="#712739"
                  stroke-width="2"
                  strokeLinecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
          </button>
        ))}

        {/* Add custom button */}
        <button
          type="button"
          className={css.addCustomButton}
          onClick={onAddCustomClick}
        >
          <svg
            width="27"
            height="27"
            viewBox="0 0 27 27"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M13.5 0C6.04432 0 0 6.04432 0 13.5C0 20.9557 6.04432 27 13.5 27C20.9557 27 27 20.9557 27 13.5C27 6.04432 20.9557 0 13.5 0ZM14.7273 18.4091C14.7273 18.7346 14.598 19.0467 14.3678 19.2769C14.1377 19.5071 13.8255 19.6364 13.5 19.6364C13.1745 19.6364 12.8623 19.5071 12.6322 19.2769C12.402 19.0467 12.2727 18.7346 12.2727 18.4091V14.7273H8.59091C8.26542 14.7273 7.95325 14.598 7.7231 14.3678C7.49294 14.1377 7.36364 13.8255 7.36364 13.5C7.36364 13.1745 7.49294 12.8623 7.7231 12.6322C7.95325 12.402 8.26542 12.2727 8.59091 12.2727H12.2727V8.59091C12.2727 8.26542 12.402 7.95326 12.6322 7.7231C12.8623 7.49294 13.1745 7.36364 13.5 7.36364C13.8255 7.36364 14.1377 7.49294 14.3678 7.7231C14.598 7.95326 14.7273 8.26542 14.7273 8.59091V12.2727H18.4091C18.7346 12.2727 19.0467 12.402 19.2769 12.6322C19.5071 12.8623 19.6364 13.1745 19.6364 13.5C19.6364 13.8255 19.5071 14.1377 19.2769 14.3678C19.0467 14.598 18.7346 14.7273 18.4091 14.7273H14.7273V18.4091Z"
              fill="#712739"
            />
          </svg>
          Add custom
        </button>
      </div>
    </div>
  );
};

AmenityCategoryContent.defaultProps = {
  selectedAmenities: [],
  customAmenities: [],
  wifiSpeeds: null,
  className: null,
  rootClassName: null,
};

AmenityCategoryContent.propTypes = {
  amenities: array.isRequired,
  selectedAmenities: array,
  customAmenities: array,
  wifiSpeeds: shape({
    download: number,
    upload: number,
  }),
  onAmenitySelect: func.isRequired,
  onAmenityDeselect: func.isRequired,
  onAddCustomClick: func.isRequired,
  onCustomAmenityRemove: func.isRequired,
  className: string,
  rootClassName: string,
};

export default AmenityCategoryContent;
